/**
 * @flow
 */

import React from 'react';
import styled from 'styled-components';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { isValidNumber } from 'libphonenumber-js';
import { Input as BaseInput, Form, Message, Icon, Label as BaseLabel } from 'semantic-ui-react';
import { FormLabel as BaseFormLabel } from '../../../../components';
import Status from './Status';
import ServiceGroupSelect from './ServiceGroupSelect';
import EmailField from './EmailField';
import PhoneField from './PhoneField';
import Gender from '../../../clients/components/form/Gender';
import styles from '../../../../styles';

const FormLabel = styled(BaseFormLabel)`
    ${styles.proximaNovaFontFamily} !important;
`;

const Input = styled(BaseInput)`
    & input {
        background-color: ${props =>
            props.readOnly ? '#fffaf3' : '#fff'} !important;
    }
`;

const intlMessages = defineMessages({
    userStatusError: {
        id: 'pages.staff.masterForm.userStatusError',
        defaultMessage: 'Select user status',
    },
    serviceGroupsError: {
        id: 'pages.staff.masterForm.serviceGroupsError',
        defaultMessage: 'Select service groups the master provide',
    },
    firstNameError: {
        id: 'pages.staff.masterForm.firstNameError',
        defaultMessage: 'Enter user first name',
    },
    phoneError: {
        id: 'pages.staff.masterForm.phoneError',
        defaultMessage: 'Invalid phone number.',
    },
    noCountryCodeError: {
        id: 'pages.staff.masterForm.noCountryCodeError',
        defaultMessage: 'Phone number should start with country code',
    },
    genderError: {
        id: 'pages.staff.masterForm.genderError',
        defaultMessage: 'Please select master gender',
    },
});

function MasterForm({ readOnly, userLookupStatus, userLookupUser, onUserLookup, resetUserLookup, intl }) {
    const { control, formState: { errors } } = useFormContext();

    const validatePhone = value => {
        if (!value) return true;
        if (value[0] !== '+') return intl.formatMessage(intlMessages.noCountryCodeError);
        if (!isValidNumber(value)) return intl.formatMessage(intlMessages.phoneError);
        return true;
    };

    return (
        <Form error={!!errors.root}>
            {userLookupUser && (
                <Message info>
                    <Icon name="info" />
                    <strong>
                        {userLookupUser.firstName} {userLookupUser.lastName}
                    </strong>{' '}
                    <FormattedMessage
                        id="pages.clients.form.ClientForm.clientExistsMessage"
                        defaultMessage="already exists in our system, you can add it to your salon"
                    />
                </Message>
            )}
            {errors.root && (
                <Message error>
                    <Icon name="warning sign" />
                    {errors.root.message}
                </Message>
            )}
            <Form.Group widths="equal">
                <Form.Field error={!!errors.phone}>
                    <FormLabel>
                        <FormattedMessage
                            id="pages.staff.form.clientForm.phone"
                            defaultMessage="Phone number"
                        />
                    </FormLabel>
                    <Controller
                        name="phone"
                        control={control}
                        rules={{ validate: validatePhone }}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <PhoneField
                                {...field}
                                onChange={(e, data) => field.onChange(data?.value ?? e)}
                                onBlur={() => {
                                    field.onBlur();
                                    if (field.value) {
                                        onUserLookup?.({ phone: field.value.replace(/[^+\d]+/g, ''), role: 'MASTER' });
                                    } else {
                                        resetUserLookup?.();
                                    }
                                }}
                                userLookupStatus={userLookupStatus}
                                disabled={readOnly}
                            />
                        )}
                    />
                    {errors.phone && (
                        <BaseLabel color="red" pointing basic>{errors.phone.message}</BaseLabel>
                    )}
                </Form.Field>
                <Form.Field error={!!errors.email}>
                    <FormLabel>
                        <FormattedMessage
                            id="pages.staff.form.clientForm.email"
                            defaultMessage="E-mail"
                        />
                    </FormLabel>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field: { ref: _ref, ...field } }) => (
                            <EmailField
                                {...field}
                                onBlur={() => {
                                    field.onBlur();
                                    if (field.value) {
                                        onUserLookup?.({ email: field.value, role: 'MASTER' });
                                    } else {
                                        resetUserLookup?.();
                                    }
                                }}
                                userLookupStatus={userLookupStatus}
                                disabled={readOnly}
                            />
                        )}
                    />
                </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
                <Form.Field error={!!errors.firstName}>
                    <FormLabel>
                        <FormattedMessage
                            id="pages.staff.form.clientForm.firstName"
                            defaultMessage="First name"
                        />
                    </FormLabel>
                    <Controller
                        name="firstName"
                        control={control}
                        rules={{
                            required: userLookupStatus !== 'EXISTS'
                                ? intl.formatMessage(intlMessages.firstNameError)
                                : false,
                        }}
                        render={({ field }) => <Input {...field} readOnly={readOnly} fluid />}
                    />
                    {errors.firstName && (
                        <BaseLabel color="red" pointing basic>{errors.firstName.message}</BaseLabel>
                    )}
                </Form.Field>
                <Form.Field>
                    <FormLabel>
                        <FormattedMessage
                            id="pages.staff.form.clientForm.lastName"
                            defaultMessage="Last name"
                        />
                    </FormLabel>
                    <Controller
                        name="lastName"
                        control={control}
                        render={({ field }) => <Input {...field} readOnly={readOnly} fluid />}
                    />
                </Form.Field>
            </Form.Group>
            <Form.Group>
                <Form.Field width={8} error={!!errors.serviceGroups}>
                    <FormLabel>
                        <FormattedMessage
                            id="pages.staff.form.clientForm.services"
                            defaultMessage="Services"
                        />
                    </FormLabel>
                    <Controller
                        name="serviceGroups"
                        control={control}
                        rules={{
                            validate: value =>
                                (!value || value.length === 0)
                                    ? intl.formatMessage(intlMessages.serviceGroupsError)
                                    : true,
                        }}
                        render={({ field: { ref: _ref, value, onChange, ...field } }) => (
                            <ServiceGroupSelect
                                {...field}
                                value={value || []}
                                onChange={(e, data) => onChange(data?.value ?? [])}
                                multiple
                            />
                        )}
                    />
                    {errors.serviceGroups && (
                        <BaseLabel color="red" pointing basic>{errors.serviceGroups.message}</BaseLabel>
                    )}
                </Form.Field>
                <Form.Field width={4} error={!!errors.status}>
                    <FormLabel>
                        <FormattedMessage
                            id="pages.staff.form.clientForm.accountStatus"
                            defaultMessage="Account status"
                        />
                    </FormLabel>
                    <Controller
                        name="status"
                        control={control}
                        rules={{ required: intl.formatMessage(intlMessages.userStatusError) }}
                        render={({ field: { value, onChange } }) => (
                            <Status
                                value={value}
                                onChange={(e, data) => onChange(data?.value ?? e)}
                            />
                        )}
                    />
                    {errors.status && (
                        <BaseLabel color="red" pointing basic>{errors.status.message}</BaseLabel>
                    )}
                </Form.Field>
                <Form.Field width={4} error={!!errors.gender}>
                    <FormattedMessage
                        id="pages.clients.form.ClientForm.gender"
                        defaultMessage="Gender"
                        children={txt => <FormLabel>{txt}</FormLabel>}
                    />
                    <Controller
                        name="gender"
                        control={control}
                        rules={{ required: intl.formatMessage(intlMessages.genderError) }}
                        render={({ field: { value, onChange } }) => (
                            <Gender
                                value={value}
                                onChange={(e, data) => onChange(data?.value ?? e)}
                                disabled={readOnly}
                            />
                        )}
                    />
                    {errors.gender && (
                        <BaseLabel color="red" pointing basic>{errors.gender.message}</BaseLabel>
                    )}
                </Form.Field>
            </Form.Group>
        </Form>
    );
}

export default injectIntl(MasterForm);
